import { Box } from '@mantine/core';
import { FC, ReactNode, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PositionDto } from '../../../Service/DocumentT';
import { DocumentContext } from '../../../Service/Context/DocumentContextV2';
import { useEventScope } from '../../../Service/hooks/useEventScope';
import { useEventData } from '../../../Service/Context/EventParticipationContextV2';
import { DocumentTemplateContext } from '../../../Service/Context/DocumentTemplateContext';
import { PositionS } from '../../../Service/PositionS';
import { AmountInput, EventAmountInput } from '../../../Organismns/tiptap/TiptapEditorInput';

interface Props {
  withLabel?: boolean;
  position: PositionDto;
  onChange: (patch: Partial<PositionDto>) => void;
  fullWidth?: boolean;
}

const COMPACT_INPUT_WIDTH = 50;
const EXPANDED_INPUT_WIDTH = 200;

interface AmountInputContainerProps {
  children: ReactNode;
  expandOnFocus: boolean;
  fullWidth: boolean;
}

const AmountInputContainer: FC<AmountInputContainerProps> = ({ children, expandOnFocus, fullWidth }) => {
  const [focused, setFocused] = useState(false);
  const { pathname } = useLocation();
  const canExpand = expandOnFocus && pathname.split('/').includes('settings');
  const expanded = canExpand && focused;

  if (fullWidth) {
    return <div style={{ flexShrink: 0 }}>{children}</div>;
  }

  return (
    <Box
      sx={{
        width: COMPACT_INPUT_WIDTH,
        minWidth: COMPACT_INPUT_WIDTH,
        height: 36,
        position: 'relative',
        flexShrink: 0,
        overflow: 'visible',
        zIndex: expanded ? 10 : undefined,
      }}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={() => setFocused(false)}
    >
      <div
        className="position-amount-input-expander"
        data-testid="position-amount-input-expander"
        data-expand-on-focus={canExpand || undefined}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: expanded ? EXPANDED_INPUT_WIDTH : COMPACT_INPUT_WIDTH,
          overflow: 'hidden',
          transition: 'width 160ms ease',
          boxShadow: expanded ? '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)' : undefined,
        }}
      >
        {children}
      </div>
    </Box>
  );
};

const PositionAmountInput: FC<Props> = ({ withLabel, position, onChange, fullWidth }) => {
  const { isEventScope } = useEventScope();

  if (isEventScope) {
    return (
      <EventPositionAmountInput withLabel={withLabel} position={position} onChange={onChange} fullWidth={fullWidth} />
    );
  }
  return (
    <TemplatePositionAmountInput withLabel={withLabel} position={position} onChange={onChange} fullWidth={fullWidth} />
  );
};

const TemplatePositionAmountInput: FC<Props> = ({ onChange, position, withLabel = false, fullWidth = false }) => {
  const { bookitupDocument: documentTemplate } = useContext(DocumentTemplateContext);
  const { bookitupDocument: document } = useContext(DocumentContext);
  const { pathname } = useLocation();
  const bookitupDocument = documentTemplate ?? document;
  const { documentType } = bookitupDocument ?? {};
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const hasAmountPlaceholder = /{[\w:-]+}/.test(position.amount ?? '');

  const saveAmount = async (amount: string) => {
    const patch: Partial<PositionDto> = { amount };

    if (amount === '{location_distance}') {
      patch.unit = 'km';
    } else if (amount === '{event_hours}') {
      patch.unit = 'Std.';
    }

    try {
      const savedPosition = pathname.includes('positions')
        ? await PositionS.editProduct(position.id, patch)
        : await PositionS.patchPosition(position.id, patch);

      if (savedPosition) {
        onChange(savedPosition);
        return true;
      }
    } catch {
      return false;
    }

    return false;
  };

  return (
    <AmountInputContainer expandOnFocus={hasAmountPlaceholder} fullWidth={fullWidth}>
      <AmountInput
        value={position.amount ?? ''}
        patchField={saveAmount}
        disabled={isPartialInvoice}
        label={withLabel ? 'Menge' : undefined}
      />
    </AmountInputContainer>
  );
};

const EventPositionAmountInput: FC<Props> = ({ onChange, position, withLabel = false, fullWidth = false }) => {
  const { bookitupDocument } = useContext(DocumentContext);
  const { distance, event } = useEventData();
  const { documentType } = bookitupDocument ?? {};
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const hasAmountPlaceholder = /{[\w:-]+}/.test(position.amount ?? '');

  const saveAmount = async (amount: string) => {
    const patch: Partial<PositionDto> = { amount };

    if (amount === '{location_distance}') {
      if (distance?.distanceInMeters) {
        patch.unit = 'km';
      }
    } else if (amount === '{event_hours}' && event?.dates[0]?.hours) {
      patch.unit = 'Std.';
    }

    try {
      const savedPosition = await PositionS.patchPosition(position.id, patch);
      if (savedPosition) {
        onChange(savedPosition);
        return true;
      }
    } catch {
      return false;
    }

    return false;
  };

  return (
    <AmountInputContainer expandOnFocus={hasAmountPlaceholder} fullWidth={fullWidth}>
      <EventAmountInput
        value={position.amount ?? ''}
        patchField={saveAmount}
        disabled={isPartialInvoice}
        label={withLabel ? 'Menge' : undefined}
      />
    </AmountInputContainer>
  );
};

export default PositionAmountInput;
